import { GlAvatarLink, GlAvatar, GlSprintf, GlLink } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { defineAsyncComponent } from '../../../lib/utils/vue3compat/vue.js?vue3';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import { confirmAction } from '../../../lib/utils/confirm_via_gl_modal/confirm_via_gl_modal.js?vue3';
import { createAlert } from '../../../alert.js?vue3';
import { ignoreWhilePending } from '~/lib/utils/ignore_while_pending';
import { __, sprintf } from '~/locale';
import { detectAndConfirmSensitiveTokens } from '../../../lib/utils/secret_detection.js?vue3';
import { isCurrentUser } from '~/lib/utils/common_utils';
import { parseAgentPresence } from '~/ai/agents_utils';
import TimeAgoTooltip from '../../../vue_shared/components/time_ago_tooltip.vue?vue3';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import { glSlotsMixin } from '~/lib/utils/vue3compat/gl_slots_mixin';
import NoteActions from './note_actions.vue?vue3';
import NoteBody from './note_body.vue?vue3';
import NoteHeader from './note_header.vue?vue3';
import TimelineEntryItem from './timeline_entry_item.vue';
export default {
    name: 'NoteableNote',
    components: {
        NoteHeader,
        NoteActions,
        NoteBody,
        DuoCodeReviewFindingRow: defineAsyncComponent(()=>import('../../../../../../ee/app/assets/javascripts/notes/components/duo_code_review_finding_row.vue?vue3')),
        NoteSessionBar: defineAsyncComponent(()=>import('../../../../../../ee/app/assets/javascripts/ai/shared/widgets/note_session_bar.vue?vue3')),
        NoteAgentActorLine: defineAsyncComponent(()=>import('../../../../../../ee/app/assets/javascripts/ai/shared/widgets/note_agent_actor_line.vue?vue3')),
        GlAvatarLink,
        GlAvatar,
        GlSprintf,
        GlLink,
        TimelineEntryItem,
        TimeAgoTooltip
    },
    mixins: [
        glSlotsMixin,
        glFeatureFlagsMixin()
    ],
    inject: {
        store: {
            type: Object
        },
        endpoints: {
            type: Object
        }
    },
    provide () {
        return {
            reportAbusePath: this.endpoints.reportAbuse
        };
    },
    props: {
        note: {
            type: Object,
            required: true
        },
        showReplyButton: {
            type: Boolean,
            required: false,
            default: false
        },
        autosaveKey: {
            type: String,
            required: false,
            default: ''
        },
        restoreFromAutosave: {
            type: Boolean,
            required: false,
            default: false
        },
        timelineLayout: {
            type: Boolean,
            required: false,
            default: false
        },
        isLastDiscussion: {
            type: Boolean,
            required: false,
            default: false
        },
        canResolve: {
            type: Boolean,
            required: false,
            default: false
        },
        isResolved: {
            type: Boolean,
            required: false,
            default: false
        },
        isResolving: {
            type: Boolean,
            required: false,
            default: false
        },
        isFirstNote: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'cancel-editing',
        'note-edited',
        'resolve',
        'start-editing',
        'start-replying'
    ],
    data () {
        return {
            actorLineOverflows: false,
            isDeleting: false,
            isSaving: false
        };
    },
    computed: {
        isEditing () {
            return this.note.isEditing;
        },
        author () {
            return this.note.author;
        },
        authorId () {
            return getIdFromGraphQLId(this.author.id);
        },
        commentType () {
            return this.note.internal ? __('internal note') : __('comment');
        },
        isDraft () {
            return Boolean(this.note.isDraft);
        },
        canAwardEmoji () {
            if (this.isDraft) return false;
            return this.note.current_user?.can_award_emoji ?? false;
        },
        canEdit () {
            return this.note.current_user?.can_edit ?? false;
        },
        canReportAsAbuse () {
            if (this.isDraft) return false;
            return Boolean(this.endpoints.reportAbuse) && !isCurrentUser(this.authorId);
        },
        resolvedText () {
            return this.note.resolved_by_push ? __('Automatically resolved %{timeago} by %{author}') : __('Resolved %{timeago} by %{author}');
        },
        isAmazonQCodeReview () {
            return this.author.username === 'amazon-q';
        },
        hasSession () {
            return this.glFeatures.noteAgentSessionBar && Boolean(this.note.duo_session_id_triggered && this.note.duo_session_agent_name);
        },
        agentPresence () {
            if (!this.glFeatures.agentPresenceConsolidation) return null;
            return parseAgentPresence(this.note.agent_presence);
        },
        duoSessionId () {
            if (this.agentPresence) return null;
            return this.note.duo_session_id;
        }
    },
    watch: {
        isEditing: {
            handler (isEditing) {
                if (isEditing) this.$nextTick(()=>this.$el.scrollIntoView({
                        block: 'nearest'
                    }));
            },
            immediate: true
        }
    },
    methods: {
        async onDelete () {
            const msg = sprintf(__('Are you sure you want to delete this %{commentType}?'), {
                commentType: this.commentType
            });
            const confirmed = await confirmAction(msg, {
                primaryBtnVariant: 'danger',
                primaryBtnText: this.note.internal ? __('Delete internal note') : __('Delete comment')
            });
            if (!confirmed) return;
            this.isDeleting = true;
            try {
                await this.store.destroyNote(this.note);
            } catch (error) {
                createAlert({
                    message: __('Something went wrong while deleting your note. Please try again.')
                });
            } finally{
                this.isDeleting = false;
            }
        },
        async saveNote (noteText) {
            const confirmSubmit = await detectAndConfirmSensitiveTokens({
                content: noteText
            });
            if (!confirmSubmit) return;
            this.isSaving = true;
            try {
                await this.store.saveNote(this.note, noteText);
                this.$emit('cancel-editing');
            } finally{
                this.isSaving = false;
            }
        },
        onCancelEditing: ignoreWhilePending(async function cancel(shouldConfirm) {
            if (shouldConfirm) {
                const msg = sprintf(__('Are you sure you want to cancel editing this %{commentType}?'), {
                    commentType: this.commentType
                });
                const confirmed = await confirmAction(msg, {
                    primaryBtnText: __('Cancel editing'),
                    primaryBtnVariant: 'danger',
                    secondaryBtnVariant: 'default',
                    secondaryBtnText: __('Continue editing'),
                    hideCancel: true
                });
                if (!confirmed) return;
            }
            this.$emit('cancel-editing');
        }),
        async toggleAward (name) {
            try {
                await this.store.toggleAwardOnNote(this.note, name);
            } catch (error) {
                createAlert({
                    message: __('Failed to set a reaction. Please try again.'),
                    error
                });
            }
        }
    }
};
